import React, { useState, useRef, useEffect } from "react";
import { Box, Typography, Paper, Chip, Grid, Button, TextField, IconButton } from "@mui/material";
import { useSelector } from "react-redux";
import ReusableDialog from "../../../../common/dialog/dialog";
import MicIcon from "@mui/icons-material/Mic";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import SelectableRadioGroup from "../../../../common/selectableRadioGroup/selectable-radio-group";

const emotions = [
  "Calm",
  "Scared",
  "Anxious",
  "Angry",
  "Upset",
  "Tense",
  "Withdrawn",
  "Relieved",
  "Defeated",
  "Disconnected",
  "Other",
];
const anythings = [
  "Apologetic",
  "Silent",
  "Defiant",
  "Cooperative",
  "Confused",
  "Grateful",
  "Hostile",
  "Dismissive",
  "Curious",
  "Indifferent",
  "Other",
];
const requests = [
  "Comfort",
  "Space",
  "Water",
  "Privacy",
  "Reassurance",
  "Apology",
  "Explanation",
  "Leave",
  "Support",
  "Food",
  "Other",
];

const behaviors = [
  "Crying",
  "Sitting",
  "Pacing",
  "Fidgeting",
  "Isolating",
  "Cooperating",
  "Aggressive",
  "Self-soothing",
  "Walking",
  "Staring",
  "Other",
];

const responses = [
  "Reassured",
  "Monitored",
  "Debriefed",
  "Comforted",
  "Observed",
  "Supported",
  "Redirected",
  "Listened",
  "Calmed",
  "Guided",
  "Other",
];

const AfterIncident = ({ updateFormData, nextStep, prevStep, formData }) => {
  const user = useSelector((state) => state.auth.user); // User data from Redux store
  const [selectedAnything, setSelectedAnything] = useState("");
  const [selectedEmotion, setSelectedEmotion] = useState("");
  const [recording, setRecording] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const [note, setNote] = useState("");
  const mediaRecorderRef = useRef(null);
  const audioChunks = useRef([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [incidentRecords, setIncidentRecords] = useState([]); // Stores submitted voice records
  const [selectedRequest, setSelectedRequest] = useState("");
  const [selectedBehavior, setSelectedBehavior] = useState("");
  const [selectedResponse, setSelectedResponse] = useState("");

  useEffect(() => {
    if (formData && formData.length > 0) {
      setIncidentRecords(formData);
    }
  }, [formData]);

  const allowedBehaviors = Array.isArray(incidentRecords) ? incidentRecords.map((b) => b.behavior) : [];

  // Start Recording
  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mediaRecorder = new MediaRecorder(stream);
    mediaRecorderRef.current = mediaRecorder;
    audioChunks.current = [];

    mediaRecorder.ondataavailable = (event) => {
      audioChunks.current.push(event.data);
    };

    mediaRecorder.onstop = () => {
      const audioBlob = new Blob(audioChunks.current, { type: "audio/wav" });
      const audioUrl = URL.createObjectURL(audioBlob);
      setAudioURL(audioUrl);
    };

    mediaRecorder.start();
    setRecording(true);
  };

  // Stop Recording
  const stopRecording = () => {
    mediaRecorderRef.current.stop();
    setRecording(false);
  };

  const newBehavior = selectedEmotion
    ? selectedEmotion
    : selectedAnything
    ? selectedAnything
    : selectedRequest
    ? selectedRequest
    : selectedBehavior
    ? selectedBehavior
    : selectedResponse;

  // Handle Submit - Save Recorded Entry
  const handleSubmit = async () => {
    // Check for duplicates
    const isDuplicate = incidentRecords.some((record) => record.behavior === newBehavior);
    if (isDuplicate) {
      alert("This behavior has already been recorded.");
      return;
    }

    let uploadedAudioUrl = null;

    try {
      // Upload audio if available
      if (audioURL) {
        const response = await fetch(audioURL);
        const blob = await response.blob();

        const formData = new FormData();
        formData.append("audio", blob, "recording.wav");

        const uploadRes = await fetch("/api/audio", {
          method: "POST",
          body: formData,
        });

        const { audioUrl } = await uploadRes.json();
        uploadedAudioUrl = audioUrl;
      }

      // Require at least note or audio
      if (!uploadedAudioUrl && !note.trim()) {
        alert("Please provide a note or an audio recording.");
        return;
      }

      const newRecord = {
        behavior: newBehavior,
        audio: uploadedAudioUrl,
        note: note,
      };

      setIncidentRecords([...incidentRecords, newRecord]);
      setDialogOpen(false);
      setAudioURL(null);
      setNote("");
      setSelectedEmotion("");
      setSelectedAnything("");
      setSelectedRequest("");
      setSelectedBehavior("");
      setSelectedResponse("");
    } catch (err) {
      console.error("Upload or submission failed", err);
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
    setNote("");
    setAudioURL(null);
    setSelectedEmotion(""); // Reset criteria after submission
    setSelectedAnything(""); // Reset behavior after submission
    setSelectedRequest("");
    setSelectedBehavior("");
    setSelectedResponse("");
  };

  // Handle Deleting a Record
  const handleDeleteRecord = (index) => {
    const updatedRecords = incidentRecords.filter((_, i) => i !== index);
    setIncidentRecords(updatedRecords);
  };

  return (
    <Box sx={{ pl: 4, pr: 4, ml: 2 }}>
      <Chip
        variant='contained'
        sx={{
          fontSize: "14px",
          backgroundColor: "#F2F6FF",
          color: "blue",
        }}
        label='Daily Log'
        size='small'
      />
      <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold" }}>Non - Physical Intervention</Typography>
      <Typography variant='subtitle2' sx={{ color: "#757575" }}>
        Date: {new Date().toLocaleDateString()} &nbsp; • &nbsp;{" "}
        <span style={{ color: "#1751D0" }}>{user.username}</span>
      </Typography>
      <Box sx={{ borderBottom: "1px solid #E0E0E0", mt: 2, mb: 2 }} />

      <Grid container spacing={2}>
        <Grid item lg={8} md={8}>
          <Typography sx={{ fontSize: "20px", fontWeight: "bold", color: "black" }}>
            How was the young person after the incident ?
          </Typography>
          <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "black" }}>Emotions</Typography>
          <Typography sx={{ fontSize: "14px", mb: 2 }}>
            How was the young person post physical intervention of emotions
          </Typography>
          <SelectableRadioGroup
            options={emotions}
            selectedValue={selectedEmotion}
            setSelectedValue={setSelectedEmotion}
            setDialogOpen={setDialogOpen}
            allowedOptions={allowedBehaviors}
          />

          <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "black", mt: 3 }}>Anything Said</Typography>
          <Typography sx={{ fontSize: "14px", mb: 2 }}>
            How was the young person post physical intervention of anything said
          </Typography>

          <SelectableRadioGroup
            options={anythings}
            selectedValue={selectedAnything}
            setSelectedValue={setSelectedAnything}
            setDialogOpen={setDialogOpen}
            allowedOptions={allowedBehaviors}
          />

          <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "black", mt: 3 }}>Any Requests</Typography>
          <Typography sx={{ fontSize: "14px", mb: 2 }}>
            How was the young person post physical intervention of any requests
          </Typography>

          <SelectableRadioGroup
            options={requests}
            selectedValue={selectedRequest}
            setSelectedValue={setSelectedRequest}
            setDialogOpen={setDialogOpen}
            allowedOptions={allowedBehaviors}
          />

          <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "black", mt: 3 }}>
            What were they doing ?
          </Typography>
          <Typography sx={{ fontSize: "14px", mb: 2 }}>How was the young person post physical intervention</Typography>

          <SelectableRadioGroup
            options={behaviors}
            selectedValue={selectedBehavior}
            setSelectedValue={setSelectedBehavior}
            setDialogOpen={setDialogOpen}
            allowedOptions={allowedBehaviors}
          />

          <Typography sx={{ fontSize: "16px", fontWeight: "bold", color: "black", mt: 3 }}>
            What actions staff took ?
          </Typography>
          <Typography sx={{ fontSize: "14px", mb: 2 }}>
            How was the young person post physical intervention of any actions staff took
          </Typography>

          <SelectableRadioGroup
            options={responses}
            selectedValue={selectedResponse}
            setSelectedValue={setSelectedResponse}
            setDialogOpen={setDialogOpen}
            allowedOptions={allowedBehaviors}
          />

          <ReusableDialog
            isOpen={dialogOpen}
            onClose={handleClose}
            title={newBehavior}
            onSubmit={handleSubmit}
            submitText='Submit'
            cancelText='Cancel'
            disable={!audioURL && !note}
          >
            {/* Description */}
            <Typography variant='body2' sx={{ color: "#757575", mb: 2 }}>
              Describe how they are putting themselves at risk of injury.
            </Typography>

            {/* Audio Recording UI */}
            <Box
              sx={{
                border: "2px dashed #ccc",
                padding: 3,
                textAlign: "center",
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 180,
                mb: 2,
              }}
            >
              {!audioURL ? (
                <>
                  <Typography sx={{ color: "#757575", fontSize: "16px", mb: 1 }}>
                    Try Recording Your Sound Now
                  </Typography>
                  <MicIcon sx={{ fontSize: 50, color: "#757575", mb: 1 }} />
                  <Typography sx={{ fontSize: "14px", color: "#757575", mb: 1 }}>
                    To start recording press the <b>“Start Recording”</b> button and start recording!
                  </Typography>
                  <Button variant='contained' color='primary' onClick={recording ? stopRecording : startRecording}>
                    {recording ? "Stop Recording" : "Start Recording"}
                  </Button>
                </>
              ) : (
                <>
                  <audio controls src={audioURL} />
                  <Button variant='contained' color='primary' sx={{ mt: 2 }} onClick={() => setAudioURL(null)}>
                    Record Again
                  </Button>
                </>
              )}
            </Box>

            {/* Notes Field */}
            <Typography sx={{ fontSize: "14px", mb: 1 }}>If any note,</Typography>
            <TextField
              fullWidth
              multiline
              rows={2}
              placeholder='Enter your note here...'
              variant='outlined'
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </ReusableDialog>
        </Grid>
        <Grid item lg={4} md={4}>
          {/*  Non - Physical Intervention Details Section - Recorded Entries */}
          <Box sx={{ mt: 4 }}>
            <Typography sx={{ fontSize: "14px", color: "black", fontWeight: "bold", mb: 2 }}>
              Non - Physical Intervention
            </Typography>
            <Typography sx={{ fontSize: "14px", mb: 3 }}>Behavior Records</Typography>

            {incidentRecords.length > 0 ? (
              <>
                {[
                  { title: "Emotions", data: emotions },
                  { title: "Anything Said", data: anythings },
                  { title: "Any Requests", data: requests },
                  { title: "What were they doing", data: behaviors },
                  { title: "What actions staff took", data: responses },
                ].map((category, index) => {
                  const filteredRecords = incidentRecords.filter((record) => category.data.includes(record.behavior));

                  if (filteredRecords.length === 0) return null; // Skip category if no matching records

                  return (
                    <Box key={index} sx={{ mt: 2 }}>
                      <Typography sx={{ fontSize: "14px", fontWeight: "bold", mb: 1, color: "black" }}>
                        {category.title}
                      </Typography>
                      {filteredRecords.map((record, recordIndex) => (
                        <Paper
                          key={recordIndex}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            padding: 1,
                            mb: 1,
                            borderRadius: 2,
                            border: "1px solid #ccc",
                          }}
                        >
                          <Typography sx={{ fontSize: "14px", color: "black" }}>
                            {record.audio ? (
                              record.behavior
                            ) : (
                              <>
                                {record.behavior}
                                <br />
                                {record.note}
                              </>
                            )}
                          </Typography>

                          <Box>
                            {record.audio && (
                              <>
                                <IconButton onClick={() => window.open(record.audio, "_blank")}>
                                  <MicIcon color='primary' />
                                </IconButton>
                                <IconButton onClick={() => window.open(record.audio, "_blank")}>
                                  <CloudDownloadIcon color='primary' />
                                </IconButton>
                              </>
                            )}
                            <IconButton onClick={() => handleDeleteRecord(index)}>
                              <DeleteIcon color='error' />
                            </IconButton>
                          </Box>
                        </Paper>
                      ))}
                    </Box>
                  );
                })}
              </>
            ) : (
              <Typography sx={{ fontSize: "14px", color: "gray" }}>No incident records found.</Typography>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Footer Actions */}
      <Box
        sx={{
          position: "fixed",
          bottom: 20,
          right: 20,
          zIndex: 1000,
          display: "flex",
          gap: 2,
        }}
      >
        <Button
          variant='outlined'
          color='primary'
          onClick={() => {
            window.scrollTo(0, 0); // Prevent scrolling
            prevStep();
          }}
        >
          Previous
        </Button>

        <Button
          variant='contained'
          color='primary'
          onClick={() => {
            window.scrollTo(0, 0); // Prevent scrolling
            nextStep(); // Call the function properly
            updateFormData({ afterIncidentInfo: incidentRecords }); // Call the function properly
          }}
          disabled={
            !incidentRecords.some((r) => emotions.includes(r.behavior)) ||
            !incidentRecords.some((r) => anythings.includes(r.behavior)) ||
            !incidentRecords.some((r) => requests.includes(r.behavior)) ||
            !incidentRecords.some((r) => behaviors.includes(r.behavior)) ||
            !incidentRecords.some((r) => responses.includes(r.behavior))
          }
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

export default AfterIncident;
